<template>
  <CContainer fluid class="grid-stripped">
    <!-- Job Type Selection -->
    <PFRow :label="$pgettext('email_schedules', 'Schedule Type')">
      <PSelectKeyValue
        v-if="!edit"
        :options="jobTypeOptions"
        :value.sync="form.schedule_type"
        label="label"
        track-by="value"/>
      <CInput
        v-else
        :value="getJobTypeLabel(form.schedule_type)"
        disabled/>
      <small class="text-muted">
        <translate translate-context="email_schedules">
          Select the type of email job to schedule
        </translate>
      </small>
    </PFRow>

    <!-- Enabled Toggle -->
    <PFRow :label="$pgettext('email_schedules', 'Enabled')">
      <CSwitch
        color="success"
        :checked.sync="form.enabled"
        variant="3d"
        size="lg"/>
      <small class="text-muted">
        <translate translate-context="email_schedules">
          Enable or disable this schedule
        </translate>
      </small>
    </PFRow>

    <!-- Job Description -->
    <div v-if="form.schedule_type && jobTypeDescription" class="alert alert-secondary mb-3">
      <small>{{ jobTypeDescription }}</small>
    </div>

    <!-- Dynamic Job Settings -->
    <JobSettingsFields
      v-if="form.schedule_type"
      :form="form"
      :job-type="form.schedule_type"/>

    <!-- Email Types Used -->
    <hr class="my-4">
    <h5><translate translate-context="email_schedules">Email Types</translate></h5>
    <p class="text-muted">
      <translate translate-context="email_schedules">
        This job will send the following email types. Make sure you have configured templates for these emails.
      </translate>
    </p>

    <div v-if="emailTypesForJob.length > 0" class="email-types-list mb-3">
      <CBadge
        v-for="emailType in emailTypesForJob"
        :key="emailType"
        color="info"
        class="mr-2 mb-2">
        {{ getEmailTypeLabel(emailType) }}
      </CBadge>
    </div>
    <div v-else class="text-muted">
      <translate translate-context="email_schedules">
        No email types configured for this job
      </translate>
    </div>
  </CContainer>
</template>

<script>
import PFRow from '@/domain/core/components/PFRow.vue'
import PSelectKeyValue from '@/domain/core/components/PSelectKeyValue.vue'
import JobSettingsFields from './JobSettingsFields.vue'
import {
  getJobTypeOptions,
  getJobTypeLabel,
  getJobTypeByClass,
  getEmailTypesForJob
} from '@/domain/core/constant/emailJobTypes'
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'EmailScheduleForm',
  components: {
    PFRow,
    PSelectKeyValue,
    JobSettingsFields
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    edit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save'],
  data() {
    return {
      form: {
        schedule_type: '',
        enabled: true,
        schedule_from: null,
        schedule_to: null,
        send_time: '14:00',
        send_every: null,
        params: {}
      }
    }
  },
  computed: {
    ...mapGetters('emailTemplates', ['availableTypeByValue']),

    jobTypeOptions() {
      return getJobTypeOptions()
    },
    jobTypeDescription() {
      if (!this.form.schedule_type) return null
      const jobType = getJobTypeByClass(this.form.schedule_type)
      return jobType ? jobType.description : null
    },
    emailTypesForJob() {
      if (!this.form.schedule_type) return []
      return getEmailTypesForJob(this.form.schedule_type)
    }
  },
  mounted() {
    this.onReset()
  },
  methods: {
    getJobTypeLabel(jobType) {
      return getJobTypeLabel(jobType)
    },
    getEmailTypeLabel(emailType) {
      const emailTypeConfig = this.availableTypeByValue(emailType)
      return emailTypeConfig ? emailTypeConfig.label : emailType
    },
    onReset() {
      this.form = { ...this.data }

      // Ensure params exists (backend uses 'params')
      if (!this.form.params) {
        this.form.params = {}
      }

      // Convert send_time from UTC to local timezone for display
      if (this.form.send_time) {
        this.form.send_time = this.utcToLocal(this.form.send_time)
      }
    },
    async onSave() {
      // Convert send_time from local to UTC before sending
      const sendTimeUtc = this.form.send_time ? this.localToUtc(this.form.send_time) : null

      // Prepare data for backend - only send fields that backend expects
      const dataToSend = {
        schedule_type: this.form.schedule_type,
        enabled: this.form.enabled,
        schedule_from: this.form.schedule_from,
        schedule_to: this.form.schedule_to,
        send_time: sendTimeUtc,
        send_every: this.form.send_every,
        params: this.form.params || {}
      }

      this.$emit('save', dataToSend)
    },
    /**
     * Convert time string from UTC to local timezone
     * @param {string} timeStr - Time in HH:mm format (UTC)
     * @returns {string} - Time in HH:mm format (local)
     */
    utcToLocal(timeStr) {
      if (!timeStr) return timeStr
      const today = moment.utc().format('YYYY-MM-DD')
      const utcMoment = moment.utc(`${today} ${timeStr}`, 'YYYY-MM-DD HH:mm')
      return utcMoment.local().format('HH:mm')
    },
    /**
     * Convert time string from local timezone to UTC
     * @param {string} timeStr - Time in HH:mm format (local)
     * @returns {string} - Time in HH:mm format (UTC)
     */
    localToUtc(timeStr) {
      if (!timeStr) return timeStr
      const today = moment().format('YYYY-MM-DD')
      const localMoment = moment(`${today} ${timeStr}`, 'YYYY-MM-DD HH:mm')
      return localMoment.utc().format('HH:mm')
    }
  }
}
</script>

<style scoped>
.grid-stripped {
  padding: 0;
}

hr {
  border-top: 1px solid #dee2e6;
}

h5 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
}

.email-types-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.alert {
  border-radius: 0.25rem;
}
</style>
